import { PillTheme } from "@redotech/redo-model/pill-theme";
import { memo, useMemo } from "react";
import { BadgeRedoListItem } from "../arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "../arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import {
  BadgePillThemeToColorMapping,
  BadgePillThemeToTypeMapping,
} from "./tag-pill";

export const PillThemeNames: Record<PillTheme, string> = {
  [PillTheme.NORMAL]: "Gray",
  [PillTheme.PRIMARY_LIGHT]: "Purple",
  [PillTheme.LIGHT_PINK]: "Pink",
  [PillTheme.DANGER]: "Orange",
  [PillTheme.NEUTRAL_YELLOW]: "Yellow",
  [PillTheme.SUCCESS]: "Green",
  [PillTheme.NEUTRAL_BLUE]: "Blue",
  [PillTheme.SOLID_RED]: "Solid red",
  [PillTheme.SOLID_YELLOW]: "Solid yellow",
  [PillTheme.SOLID_GREEN]: "Solid green",
  [PillTheme.SOLID_NAVY]: "Solid navy",
  [PillTheme.SOLID_LIGHT_BLUE]: "Solid blue",
  [PillTheme.SOLID_PINK]: "Solid pink",
  [PillTheme.SOLID_ORANGE]: "Solid orange",
  [PillTheme.SOLID_BLACK]: "Solid black",
  [PillTheme.PRIMARY]: "Solid purple",
  [PillTheme.SOLID_WHITE]: "Solid white",
  [PillTheme.BRAND]: "Brand",
  [PillTheme.GRAY_BLUE]: "Gray blue",
  [PillTheme.WHITE_SUCCESS]: "White success",
  [PillTheme.GRAY_ALTERNATE]: "Gray (dark)",
  [PillTheme.BRAND_ALTERNATE]: "Brand (dark)",
  [PillTheme.ERROR_ALTERNATE]: "Red (dark)",
  [PillTheme.WARNING_ALTERNATE]: "Yellow (dark)",
  [PillTheme.SUCCESS_ALTERNATE]: "Green (dark)",
  [PillTheme.GRAY_BLUE_ALTERNATE]: "Gray blue (dark)",
  [PillTheme.BLUE_LIGHT_ALTERNATE]: "Blue light (dark)",
  [PillTheme.BLUE_ALTERNATE]: "Blue (dark)",
  [PillTheme.INDIGO_ALTERNATE]: "Indigo (dark)",
  [PillTheme.PURPLE_ALTERNATE]: "Purple (dark)",
  [PillTheme.PINK_ALTERNATE]: "Pink (dark)",
  [PillTheme.ORANGE_ALTERNATE]: "Orange (dark)",
  [PillTheme.BLACK_ALTERNATE]: "Black (dark)",
  [PillTheme.WHITE_ALTERNATE]: "White (dark)",
  [PillTheme.WHITE_SUCCESS_ALTERNATE]: "White success (dark)",
};

export const PillColorPicker = memo(function PillColorPicker({
  value,
  setValue,
  options = Object.values(PillTheme),
  label,
}: {
  value: PillTheme | undefined;
  setValue(theme: PillTheme): void;
  options?: PillTheme[];
  label?: string;
}) {
  const listItems = useMemo<BadgeRedoListItem<PillTheme>[]>(
    () =>
      options.map((option) => ({
        id: option,
        type: "badge",
        value: option,
        badge: {
          color: BadgePillThemeToColorMapping[option],
          type: BadgePillThemeToTypeMapping[option],
          text: PillThemeNames[option],
        },
      })),
    [options],
  );

  const selectedItem = useMemo(
    () => listItems.find((item) => item.value === value),
    [listItems, value],
  );

  return (
    <RedoSingleSelectDropdownInput
      dropdownPlacement="bottom-start"
      flip={false}
      label={label}
      options={listItems}
      optionSelected={({ item }) => setValue(item.value)}
      placeholder="Select a color"
      selectedItem={selectedItem}
    />
  );
});
